/* =========================================================
   CHILDREN OF DANTE · live sky
   A golden-hour sky over a sea of clouds, drawn in WebGL.
   Usage: SKY.mount(canvas, { onSnapshot(dataUrl) {} })
   Falls back to a CSS gradient if WebGL isn't available.
   ========================================================= */
window.SKY = (() => {
  const VERT = `attribute vec2 p; void main(){ gl_Position = vec4(p, 0., 1.); }`;

  const FRAG = `
  precision highp float;
  uniform vec2 uRes;
  uniform float uTime;
  uniform vec2 uMouse;   // -0.5..0.5
  uniform float uScroll; // 0..1 through the hero

  float hash(vec2 p){ p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
  float noise(vec2 p){
    vec2 i = floor(p), f = fract(p);
    vec2 u = f * f * (3. - 2. * f);
    return mix(mix(hash(i), hash(i + vec2(1., 0.)), u.x), mix(hash(i + vec2(0., 1.)), hash(i + vec2(1., 1.)), u.x), u.y);
  }
  float fbm(vec2 p){
    float v = 0., a = .5;
    mat2 R = mat2(.8, .6, -.6, .8);
    for (int i = 0; i < 5; i++){ v += a * noise(p); p = R * p * 2.03; a *= .5; }
    return v;
  }

  void main(){
    vec2 uv = gl_FragCoord.xy / uRes;          // y up
    float asp = uRes.x / uRes.y;
    float hz = .34 + uMouse.y * .025 + uScroll * .18;  // horizon line

    // ---- sky gradient: deep cobalt top -> blue -> warm peach at the horizon
    vec3 cTop = vec3(.035, .10, .32);
    vec3 cMid = vec3(.20, .42, .86);
    vec3 cLow = vec3(.98, .74, .52);
    float y = uv.y;
    vec3 col = mix(cLow, cMid, smoothstep(hz - .02, hz + .38, y));
    col = mix(col, cTop, smoothstep(hz + .30, 1.08, y));

    // ---- sun just above the cloud sea
    vec2 sp = vec2(.5 + uMouse.x * .04, hz + .055);
    float d = length((uv - sp) * vec2(asp, 1.));
    col += vec3(1., .78, .52) * exp(-d * 5.5) * .55;
    col += vec3(1., .93, .78) * exp(-d * 38.) * .9;

    // ---- soft cumulus puffs floating in the sky (sparse, drifting)
    if (y > hz) {
      vec2 q = vec2(uv.x * asp * 2.2 + uTime * .012 + uMouse.x * .08, (y - hz) * 4.4 + .5);
      vec2 w = vec2(fbm(q * .9 + 3.1), fbm(q * .9 + 7.4));
      float c = fbm(q + w * .9);
      float puff = smoothstep(.50, .72, c) * smoothstep(hz + .02, hz + .16, y) * (1. - smoothstep(.62, .98, y));
      float lightSide = clamp((c - fbm(q + w * .9 + vec2(-.06, -.09))) * 9. + .55, 0., 1.);
      vec3 pc = mix(vec3(.70, .72, .88), mix(vec3(1., .88, .76), vec3(1.), smoothstep(hz, hz + .4, y)), lightSide);
      col = mix(col, pc, puff * .85);
    }

    // ---- sea of clouds below the horizon (perspective plane, puffy + sun-lit)
    float below = hz + .012 - y;
    if (below > 0.) {
      float depth = .14 / below;
      vec2 pp = vec2((uv.x - .5 - uMouse.x * .03) * asp * depth * 2.6, depth * 3.2 + uTime * .09);
      vec2 w2 = vec2(fbm(pp * .7 + 1.3), fbm(pp * .7 + 8.2));
      float d1 = fbm(pp * 1.1 + w2 * 1.2);
      float lit = clamp((d1 - fbm(pp * 1.1 + w2 * 1.2 + vec2(0., .10))) * 7. + .55, 0., 1.);
      float tops = smoothstep(.34, .66, d1);
      float near = smoothstep(0., .30, below);
      vec3 sunLit = mix(vec3(1., .82, .64), vec3(1., .99, .97), near);
      vec3 shade = mix(vec3(.66, .62, .80), vec3(.60, .69, .92), near);
      vec3 gaps = mix(vec3(.55, .58, .80), vec3(.36, .48, .82), near);
      vec3 cloud = mix(gaps, mix(shade, sunLit, lit), tops);
      // glow on the cloud tops nearest the sun
      cloud += vec3(1., .80, .55) * exp(-length((uv - vec2(sp.x, hz)) * vec2(asp * .55, 4.)) * 2.6) * .45;
      float haze = exp(-.05 * depth);
      cloud = mix(mix(col, vec3(1., .86, .74), .5), cloud, .35 + .65 * haze);
      col = mix(col, cloud, smoothstep(0., .02, below));
    }

    // ---- soft vignette + subtle film grain
    vec2 vv = uv - .5;
    col *= 1. - dot(vv, vv) * .45;
    col += (hash(gl_FragCoord.xy + uTime) - .5) * .025;

    gl_FragColor = vec4(col, 1.);
  }`;

  function mount(canvas, opts = {}) {
    const gl = canvas.getContext("webgl", { antialias: false, preserveDrawingBuffer: true, alpha: false, depth: false, stencil: false, powerPreference: "low-power" });
    if (!gl) { canvas.classList.add("sky--fallback"); return null; }
    const sh = (type, src) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
      return s;
    };
    let prog;
    try {
      prog = gl.createProgram();
      gl.attachShader(prog, sh(gl.VERTEX_SHADER, VERT));
      gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FRAG));
      gl.linkProgram(prog);
      gl.useProgram(prog);
    } catch (e) {
      console.warn("Sky shader failed, using fallback", e);
      canvas.classList.add("sky--fallback");
      return null;
    }
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "p");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const uRes = gl.getUniformLocation(prog, "uRes");
    const uTime = gl.getUniformLocation(prog, "uTime");
    const uMouse = gl.getUniformLocation(prog, "uMouse");
    const uScroll = gl.getUniformLocation(prog, "uScroll");

    // Render well below screen resolution (clouds are soft, nobody can tell) and adapt to the device.
    let scale = opts.scale || (innerWidth < 760 ? 0.26 : 0.34);
    const size = () => {
      const r = canvas.getBoundingClientRect();
      canvas.width = Math.max(2, Math.round(r.width * scale));
      canvas.height = Math.max(2, Math.round(r.height * scale));
      gl.viewport(0, 0, canvas.width, canvas.height);
      if (typeof draw === "function") draw(performance.now());
    };
    // keep the drawing buffer matched to the element (handles resizes, zoom, svh changes)
    if ("ResizeObserver" in window) new ResizeObserver(size).observe(canvas);
    else addEventListener("resize", size);

    const state = { mx: 0, my: 0, tx: 0, ty: 0, scroll: 0 };
    const t0 = performance.now() - 40000; // start mid-drift
    let snapped = false;
    // only draw while the sky is on screen (cheap check, no observer state to get stuck)
    const onScreen = () => { const r = canvas.getBoundingClientRect(); return r.bottom > 0 && r.top < innerHeight && r.width > 0; };
    const reduced = /[?&]motion=0/.test(location.search);

    var draw = (now) => {
      if (!Number.isFinite(state.mx + state.my)) { state.mx = state.my = 0; }
      state.mx += (state.tx - state.mx) * 0.05;
      state.my += (state.ty - state.my) * 0.05;
      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.uniform1f(uTime, reduced ? 40 : (now - t0) / 1000);
      gl.uniform2f(uMouse, state.mx, state.my);
      gl.uniform1f(uScroll, state.scroll);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      if (!snapped && opts.onSnapshot) {
        snapped = true;
        try { opts.onSnapshot(canvas.toDataURL("image/jpeg", 0.85)); } catch (e) {}
      }
    };
    size();
    // 30fps cap + automatic quality drop on slow GPUs; pauses when hidden or scrolled away.
    let last = 0, slow = 0, samples = 0;
    const loop = (now) => {
      if (!reduced) requestAnimationFrame(loop);
      if (document.hidden || !onScreen() || now - last < 33) return;
      const t = performance.now();
      draw(now);
      last = now;
      if (samples < 90) {
        samples++;
        gl.finish();
        if (performance.now() - t > 14) slow++;
        if (samples === 30 && slow > 15 && scale > 0.2) { scale = Math.max(0.18, scale * 0.65); size(); slow = 0; samples = 0; }
      }
    };
    requestAnimationFrame(loop);

    return {
      pointer(nx, ny) { if (Number.isFinite(nx) && Number.isFinite(ny)) { state.tx = nx; state.ty = ny; } },
      scroll(p) { state.scroll = Number.isFinite(p) ? Math.min(1, Math.max(0, p)) : 0; if (reduced) draw(performance.now()); },
    };
  }

  return { mount };
})();
